import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import { toast } from 'react-toastify';
import API_URL from '../../config';
import { PageHeader } from '../ui';
import {
  CheckCircle,
  Circle,
  FileDown,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  HelpCircle,
} from 'lucide-react';
import '../../App.css';

const CRITICAL_MODULE_NAMES = [
  'Driver Qualification',
  'Vehicle Maintenance',
  'Drug & Alcohol',
  'Insurance',
  'Accident Register',
];

/** CFR display lines ordered most-specific first; fallback 49 CFR §390 */
const CFR_LABEL_RULES = [
  [/road test/i, '49 CFR §391.31'],
  [/employment application/i, '49 CFR §391.21'],
  [/annual review of driving record|annual review/i, '49 CFR §391.25'],
  [/motor vehicle record|\bmvr\b/i, '49 CFR §391.25'],
  [/medical examiner|medical card|medical certificate/i, '49 CFR §391.43'],
  [/cdl information|\bcdl\b/i, '49 CFR §383'],
  [/clearinghouse registration|fmcsr clearinghouse registration/i, '49 CFR §382.701'],
  [/consent to employer|consent.*query/i, '49 CFR §382.701'],
  [/pre-employment drug/i, '49 CFR §382.301'],
  [/random testing/i, '49 CFR §382.305'],
  [/supervisor training/i, '49 CFR §382.603'],
  [/consortium|tpa proof/i, '49 CFR §382.305'],
  [/inspection certificate|annual vehicle inspection|annual inspection/i, '49 CFR §396.17'],
  [/dvir|daily vehicle inspection/i, '49 CFR §396.11'],
  [/maintenance record/i, '49 CFR §396.3'],
  [/vehicle registration current/i, '49 CFR §396.3'],
  [/dot authority|operating authority|mcp\b/i, '49 CFR §365'],
  [/authority status active/i, '49 CFR §365'],
  [/liability insurance|insurance certificate|mcs-90/i, '49 CFR §387.7'],
  [/boc-3/i, '49 CFR §366'],
  [/mcs-150/i, '49 CFR §390.19'],
  [/ucr registration|unified carrier/i, '49 CFR §390'],
  [/accident register maintained|accident register/i, '49 CFR §390.15'],
  [/post-accident drug|post-accident/i, '49 CFR §382.303'],
];

function citationForItemLabel(label) {
  for (const [re, cfr] of CFR_LABEL_RULES) {
    if (re.test(label)) return cfr;
  }
  return '49 CFR §390';
}

const SECTION_DISPLAY = {
  dqf: { title: 'Driver Records', sub: 'Driver Qualification File (DQF)' },
  vmf: { title: 'Vehicle Records', sub: 'Vehicle Maintenance File (VMF)' },
  drug_alcohol: { title: 'Drug & Alcohol Program', sub: null },
  insurance_authority: { title: 'Insurance & Authority', sub: null },
  accident_register: { title: 'Accident Register', sub: null },
};

function sectionDisplayForChecklist(cl) {
  return SECTION_DISPLAY[cl.id] || { title: cl.name, sub: null };
}

function sectionStatusLabel(percent) {
  if (percent === 0) return { text: 'Not started', tone: 'danger' };
  if (percent >= 1 && percent <= 59) return { text: 'In progress', tone: 'amber' };
  if (percent >= 60 && percent <= 79) return { text: 'Needs attention', tone: 'amber' };
  if (percent >= 80 && percent <= 99) return { text: 'Almost there', tone: 'success' };
  return { text: 'Complete', tone: 'success' };
}

export default function ChecklistsPage() {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [auditPacketLoading, setAuditPacketLoading] = useState(false);
  const [auditReadyMode, setAuditReadyMode] = useState(false);

  const filteredChecklists = useMemo(() => {
    if (!auditReadyMode) return checklists;
    return checklists.filter((cl) =>
      CRITICAL_MODULE_NAMES.some((name) => cl.name && cl.name.includes(name)),
    );
  }, [checklists, auditReadyMode]);

  const overallCompletionPercent = useMemo(() => {
    if (!checklists.length) return 0;
    const sum = checklists.reduce((acc, c) => acc + c.completionPercent, 0);
    return Math.round(sum / checklists.length);
  }, [checklists]);

  const fetchChecklists = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/checklists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChecklists(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load checklists.');
      setChecklists([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  const handleAuditPacket = async () => {
    setAuditPacketLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/audit-packet/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sovrynn-audit-packet.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      posthog.capture('audit_packet_downloaded');
    } catch {
      toast.warning('Could not generate your Audit Packet. Please try again.');
    } finally {
      setAuditPacketLoading(false);
    }
  };

  if (loading && !checklists.length) {
    return (
      <div className="checklists-page">
        <div className="checklists-loading">
          <RefreshCw size={28} className="spin" />
          <p>Loading checklists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checklists-page page-container">
      <PageHeader
        title="Audit Readiness"
        subtitle="See exactly what an FMCSA auditor looks for — and where you stand right now."
        secondaryAction={{ label: 'Refresh', icon: RefreshCw, onClick: fetchChecklists }}
      />

      <div className="checklists-dri-banner card">
        <div className="checklists-dri-banner__copy">
          <p className="checklists-dri-banner__lead">
            Your checklist completion directly affects your DRI Score™. Complete missing items to improve your score.
          </p>
          <p className="checklists-dri-banner__metric">
            Overall audit readiness: <strong>{overallCompletionPercent}%</strong> complete
          </p>
        </div>
        <div className="checklists-dri-banner__actions">
          <Link to="/dashboard" className="checklists-dri-banner__link">
            View your DRI Score™ →
          </Link>
          <button
            type="button"
            className="btn btn-primary checklists-dri-banner__packet-btn"
            onClick={handleAuditPacket}
            disabled={auditPacketLoading}
            title="Download a formatted summary of your compliance records — ready to present at audit"
          >
            <FileDown size={18} aria-hidden />
            {auditPacketLoading ? 'Preparing...' : 'Download Audit Packet'}
          </button>
        </div>
      </div>

      <div className="checklists-audit-switch-row">
        <div className="checklists-audit-switch-row__text">
          <span className="checklists-audit-switch-row__label" id="audit-ready-mode-label">
            Compliance-Verified Mode
          </span>
          <span className="checklists-audit-switch-row__hint" id="audit-ready-mode-hint">
            Show only the highest-priority items that shape your compliance standing
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={auditReadyMode}
          aria-labelledby="audit-ready-mode-label"
          aria-describedby="audit-ready-mode-hint"
          className={`checklists-audit-switch ${auditReadyMode ? 'checklists-audit-switch--on' : ''}`}
          onClick={() => setAuditReadyMode((v) => !v)}
        >
          <span className="checklists-audit-switch__thumb" aria-hidden />
        </button>
      </div>

      {auditReadyMode && (
        <div className="checklists-audit-filter-banner" role="status">
          Showing highest-priority items only. These are the records that most affect your current standing.
        </div>
      )}

      {error && (
        <div className="checklists-error" role="alert">
          {error}
        </div>
      )}

      <div className="checklists-list">
        {filteredChecklists.map((cl) => {
          const isExpanded = expandedId === cl.id;
          const { title, sub } = sectionDisplayForChecklist(cl);
          const status = sectionStatusLabel(cl.completionPercent);
          return (
            <section key={cl.id} className="checklist-module" aria-label={cl.name}>
              <button
                type="button"
                className="checklist-module-header"
                onClick={() => setExpandedId(isExpanded ? null : cl.id)}
                aria-expanded={isExpanded}
              >
                <span className="checklist-module-chevron">
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </span>
                <span className="checklist-module-name-block">
                  <span className="checklist-module-name-primary">{title}</span>
                  {sub && <span className="checklist-module-name-sub">{sub}</span>}
                </span>
                <span className={`checklist-section-status checklist-section-status--${status.tone}`}>
                  {status.text}
                </span>
                <span className={`checklist-module-progress ${cl.completionPercent === 100 ? 'complete' : ''}`}>
                  {cl.completionPercent}%
                </span>
                <span className="checklist-module-progress-bar-wrap">
                  <span className="checklist-module-progress-bar">
                    <span
                      className="checklist-module-progress-fill"
                      style={{ width: `${cl.completionPercent}%` }}
                    />
                  </span>
                </span>
              </button>
              {isExpanded && (
                <div className="checklist-module-body">
                  <ul className="checklist-items">
                    {cl.items.map((item, idx) => (
                      <li key={idx} className={`checklist-item ${item.completed ? 'completed' : ''}`}>
                        <span className="checklist-item-icon" aria-hidden>
                          {item.completed ? (
                            <CheckCircle size={20} className="icon-done" />
                          ) : (
                            <Circle size={20} className="icon-pending" />
                          )}
                        </span>
                        <div className="checklist-item-content">
                          <span className="checklist-item-label-row">
                            <span className="checklist-item-label">{item.label}</span>
                            {item.required && (
                              <span className="checklist-item-required">Required</span>
                            )}
                          </span>
                          <p className="checklist-item-citation">{citationForItemLabel(item.label)}</p>
                          {item.helpText && (
                            <p className="checklist-item-help" title={item.helpText}>
                              <HelpCircle size={14} />
                              {item.helpText}
                            </p>
                          )}
                        </div>
                        {!item.completed && (
                          <button
                            type="button"
                            className="checklist-item-docs-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/documents');
                            }}
                          >
                            Upload in Documents →
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
